import { SimulationInfo } from './../../shared/iosim-plus/models/simulationStatus';
import { AssetsSdkService } from 'app/core/assets-sdk/assets-sdk.service';
import { EventEmitter, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { IEupRxForm, PagedQueryParameters, PagedQueryResult, ReferralResponse } from '../../shared/generalInterfaces';
import { EupRoutesService } from '../../core/eupRoutes.service';
import { EMPTY, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { EupHttpHandler } from '../../core/eupHttpHandler.service';
import { FeatureToggle, GroupedStatus, RxVersion, SoftwareOptionsForCompany } from '../../shared/enums';
import { DownloadFileService } from '../../shared/downloadFile.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService, ModalKeys, ModalIcon } from '../../shared/notification/notification.service';
import { ToothNumberingSystem } from '@shared/enums';
import { IBaseOrderDataDto } from '@interfaces/IBaseOrderDataDto';
import { ICaseState } from "@interfaces/ICaseState";
import { IOrdersStatusesDto } from '@interfaces/IOrdersStatusesDto';
import { TimberService } from '@logging/timber.service';
import { DownloadStage } from '@logging/interfaces/bi.enums';
import { AssetReferenceType, createZipReader, extractFileAsBlob, getEntries } from '@itero/assets-bff-sdk'
import JSZip from '@nodeModules/jszip';
import { saveAs } from 'file-saver';
import { SpinnerService } from '@core/spinner/spinner.service';

@Injectable()
export class OrdersService {
	public orderObservable$: Observable<Order>;
	public OrderUpdated = new EventEmitter<any>();

	private readonly allowedStlExtensions = ['.stl', '.obj', '.scaninfo', '.constructioninfo', '.modelinfo']
	private readonly stlDownloadsInProgress = new Set<number>();

	constructor(
		private http: EupHttpHandler,
		private eupRoutesService: EupRoutesService,
		private downloadFileService: DownloadFileService,
		private translateService: TranslateService,
		private notificationService: NotificationService,
		private timberService: TimberService,
		private assetsSdkService: AssetsSdkService,
		private spinnerService: SpinnerService,
	) {
	}

	public getUndeliveredOrders(query: PagedQueryParameters): Observable<PagedQueryResult<IEupRxForm>> {
		return this.getOrders(this.eupRoutesService.orders.undeliveredUrl, query);
	}

	public getDeliveredOrders(query: PagedQueryParameters): Observable<PagedQueryResult> {
		return this.getOrders(this.eupRoutesService.orders.deliveredUrl, query);
	}

	public getDeliveredFrontlineOrders(query: PagedQueryParameters): Observable<PagedQueryResult> {
		return this.getOrders(this.eupRoutesService.orders.deliveredFrontlineOrdersUrl, query);
	}


	public sendPendingAttachmentsOrder(orderId: number): Observable<any> {
		return this.http.post(`${this.eupRoutesService.orders.sendPendingAttachmentsOrder}?orderId=${orderId}`);
	}

	public downloadStlFlilesOrder(orderId: number, rxId: string | undefined): Observable<void> {
		const stageReport = {
			object: "MyDesign downloading file",
			description: "downloading file from Design Suite",
			trigger: `orderId: ${orderId}`,
		}
		this.timberService.stageReportEvent({ ...stageReport, stage: DownloadStage.Started });

		const fileName = 'iTeroDesignSuite_Export_' + orderId;
		const isUseAssetsSdkSwoEnabled = this.assetsSdkService.isUseAssetsSdkSwoEnabled();
		const download$ = (isUseAssetsSdkSwoEnabled && rxId) ? this.downloadStlFilesUsingAssetsSdk(orderId, fileName, rxId) : this.downloadStlFilesLegacy(orderId, fileName);

		return download$.pipe(
			tap(() => {
				this.timberService.stageReportEvent({ ...stageReport, stage: DownloadStage.Successful });
			}),
			catchError((e) => {
				this.timberService.stageReportEvent({ ...stageReport, stage: DownloadStage.Failed });
				return this.notificationService.show(
					this.translateService.instant('Errors.StandardErrorTitle'),
					this.translateService.instant('Errors.StandardErrorMessageSingle'),
					{ buttonCombination: ModalKeys.Ok, icon: ModalIcon.Error }
				).pipe(map(() => void 0));
			})
		);
	}

	private downloadStlFilesLegacy(orderId: number, fileName: string): Observable<void> {
		const url = `${this.eupRoutesService.orders.downloadStlFiles}?orderId=${orderId}`;
		return this.downloadFileService.downloadSTLFile(url, fileName);
	}

	private downloadStlFilesUsingAssetsSdk(orderId: number, fileName: string, rxId: string | undefined): Observable<void> {
		if (this.stlDownloadsInProgress.has(orderId)) {
			return EMPTY;
		}

		return from(this.buildAndDownloadStlZip(orderId, rxId, fileName));
	}

	public deleteRx(rxId: string): Observable<any> {
		return this.http.delete(`${this.eupRoutesService.orders.deleteRxUrl}/${rxId}`, { responseType: 'text' });
	}

	private async buildAndDownloadStlZip(orderId: number, rxId: string | undefined, fileName: string): Promise<void> {
		this.stlDownloadsInProgress.add(orderId);
		this.spinnerService.start();
		try {
			const assetMetadata = await this.assetsSdkService.getAssetDownloadLink({
				rxId,
				orderId,
				assetReferenceType: AssetReferenceType.ExocadProjectFolderArchive,
			});

			const reader = await createZipReader(assetMetadata.presignedLink, assetMetadata.fileSize, { resolveLocalHeaders: true });
			const entries = getEntries(reader);
			const matching = this.getSupportedDesignEntries(entries);

			if (matching.length === 0) {
				throw new Error(`No matching design suite files found for orderId: ${orderId}`);
			}

			const zip = new JSZip();

			for (const entry of matching) {
				const blob = await extractFileAsBlob(reader, entry.filename);
				const fileName = entry.filename.split('/').pop() ?? entry.filename;
				zip.file(fileName, blob);
			}

			const zipBlob = await zip.generateAsync({ type: 'blob' });
			saveAs(zipBlob, fileName);
		} finally {
			this.spinnerService.stop();
			this.stlDownloadsInProgress.delete(orderId);
		}
	}

	private getSupportedDesignEntries(entries: Array<{ filename: string; directory: boolean }>) {
		return entries.filter(
			(entry) => !entry.directory && this.allowedStlExtensions.some((ext) => entry.filename.toLowerCase().endsWith(ext))
		);
	}

	public startProtocol(link: string, createNewiFrame: boolean): void {
		let ifrm = undefined;
		if (!createNewiFrame) {
			ifrm = document.getElementsByTagName('iframe')[0];
		}
		if (ifrm === null || ifrm === undefined) {
			ifrm = document.createElement('iframe');
		}
		ifrm.setAttribute('src', link);
		ifrm.style.display = 'none';
		document.body.appendChild(ifrm);
	}

	private getOrders(url: string, query: PagedQueryParameters): Observable<PagedQueryResult> {
		return this.http.get(url, { params: query.createSearchParams() }, true, true);
	}

	public getExportGalleryFile(orderId: string, patientName: string, rxId?: string): void {
		this.downloadFileService.downloadExportGalleryFile(
			this.eupRoutesService.orders.getScreenshotDownloadLink,
			orderId,
			patientName,
			rxId
		);
	}

	public downloadGalleryImages(orderId: string, rxId?: string): void {
		const isUseAssetsSdkSwoEnabled = this.assetsSdkService.isUseAssetsSdkSwoEnabled();
		if (isUseAssetsSdkSwoEnabled && rxId) {
			this.assetsSdkService.downloadGalleryAsZip(rxId, `iTero_Export_Gallery_${orderId ?? ''}.zip`, 'Errors.Error_download_file_body');
			return;
		}
		const params = new HttpParams().set('orderId', orderId);

		this.downloadFileWithFallback(this.eupRoutesService.orders.downloadGalleryImages, params, `iTero_Export_Gallery_${orderId}`, 'Errors.Error_download_file_body');
	}

	private downloadFileWithFallback(url: string, params: HttpParams, fileName: string, errorKey: string): void {
		this.downloadFileService
			.getFile(url, { params }, fileName)
			.subscribe({
				error: (e) => {
					this.notificationService.show(
						this.translateService.instant('Errors.Error_download_file_title'),
						this.translateService.instant(errorKey),
						{ buttonCombination: ModalKeys.Ok, icon: ModalIcon.Error }
					);
				},
			});
	}

	public getFileStorage(orderId: number): Observable<number> {
		const params = new HttpParams().set('orderId', orderId.toString());
		return this.http
			.get(this.eupRoutesService.orders.getFileStorage, { params }, true, true)
			.pipe(catchError((err) => this.handleError(err)));
	}

	public getOrderStatuses(selectedCompanyId: number, selectedDoctorId: number): Observable<IOrdersStatusesDto> {
		const params = new HttpParams().set('companyId', selectedCompanyId.toString()).set('doctorId', selectedDoctorId.toString());
		return this.http.get(this.eupRoutesService.orders.getOrderStatusesUrl, { params: params }, true, false)
			.pipe(catchError((err) => {
				this.timberService.error(err, { module: 'order.service' });
				return of({
					failedOrderMessage: '',
					isFailedOrderExists: false,
					isPendingOrders: false,
				} as IOrdersStatusesDto);
			}));
	}

	public getDoctorOrder(params: { orderId: number; doctorId: number; companyId: number }): Observable<Order> {
		const queryParams = new HttpParams()
			.set('orderId', `${params.orderId}`)
			.set('doctorId', `${params.doctorId}`)
			.set('companyId', `${params.companyId}`);
		return this.http.get(this.eupRoutesService.orders.getOrderByIdUrl, { params: queryParams });
	}

	public getBaseOrderData(params: { orderId: number }): Observable<IBaseOrderDataDto> {
		const queryParams = new HttpParams()
			.set('orderId', `${params.orderId}`);
		return this.http.get(this.eupRoutesService.orders.getBaseOrderDataUrl, { params: queryParams });
	}

	public geteReferredBaseOrderData(params: { orderId: number }): Observable<IBaseOrderDataDto> {
		const queryParams = new HttpParams()
			.set('orderId', `${params.orderId}`);
		return this.http.get(this.eupRoutesService.orders.getReferredBaseOrderDataUrl, { params: queryParams });
	}

	public getOrderCode(orderId: number): Observable<string> {
		const queryParams = new HttpParams()
			.set('orderId', `${orderId}`);
		return this.http.get(this.eupRoutesService.orders.getOrderCode, { params: queryParams });
	}

	public validateOrder(params: { id: number }): Observable<boolean> {
		const queryParams = new HttpParams()
			.set('orderId', `${params.id}`);
		return this.http.get(this.eupRoutesService.orders.validateOrderUrl, { params: queryParams });
	}

	public validateReferredOrder(params: { id: number }): Observable<boolean> {
		const queryParams = new HttpParams()
			.set('orderId', `${params.id}`);
		return this.http.get(this.eupRoutesService.orders.validateReferredOrderUrl, { params: queryParams });
	}

	public getLabOrder(params: { orderId: number | string; doctorId: number | string; companyId: number | string }): Observable<Order> {
		const queryParams = new HttpParams()
			.set('orderId', `${params.orderId}`)
			.set('doctorId', `${params.doctorId}`)
			.set('companyId', `${params.companyId}`);
		return this.http.get(this.eupRoutesService.lab.getOrderByIdUrl, { params: queryParams });
	}

	public initLabOrderObservable(params: { orderId: number; doctorId: number; companyId: number }): void {
		this.orderObservable$ = this.getLabOrder(params);
	}

	getProcedureOrCaseTypeDescription(orderOrRxForm: Order | IEupRxForm, procedureEnabled: boolean): string {
		const version = orderOrRxForm.hasOwnProperty('rxVersion')
			? (orderOrRxForm as Order).rxVersion
			: (orderOrRxForm as IEupRxForm).version;

		if (procedureEnabled && version === RxVersion.ProcedureFlow) {
			return orderOrRxForm.procedureDescription || '';
		}

		return orderOrRxForm.caseTypeDescription;
	}

	public updateLabOrderById(orderId: string, doctorId: string, companyId: string, action: (orderToEdit: Order) => void): void {
		const params = new HttpParams().set('orderId', orderId).set('doctorId', doctorId).set('companyId', companyId);
		this.http.get(this.eupRoutesService.lab.getOrderByIdUrl, { params: params }).subscribe((order: Order) => {
			if (this.OrderUpdated) {
				if (action) {
					action(order);
				}
				this.OrderUpdated.emit(order);
			}
		});
	}

	public assignOrderToDoctor(orderId: string, doctorId: number): Observable<any> {
		return this.http.post(`${this.eupRoutesService.orders.assignToDoctor}?DoctorId=${doctorId}&OriginalOrderId=${orderId}`, undefined, undefined, undefined, false).pipe(
			catchError((e) => {
				this.notificationService.show(
					this.translateService.instant('Errors.StandardErrorTitle'),
					this.translateService.instant('Errors.StandardErrorMessageSingle'),
					{ buttonCombination: ModalKeys.Ok, icon: ModalIcon.Error }
				);
				return throwError(() => e);
			})
		);
	}

	private handleError(err) {
		this.notificationService.show(
			this.translateService.instant('Errors.Error_download_file_title'),
			this.translateService.instant('Errors.Error_download_file_body'),
			{ buttonCombination: ModalKeys.Ok, icon: ModalIcon.Error }
		);

		return throwError(err);
	}
}

export class Order {
	attachmentsCount: number;
	canChangeCaseType: boolean;
	canEditRx?: boolean;
	canExportFile: boolean;
	canExportGallery: boolean;
	canOpenFile: boolean;
	canOpenWithItero?: boolean;
	canOpenWithOrthoCad?: boolean;
	canRestorativeExport: boolean;
	canReturnToDoctor?: boolean;
	canSendToMilling?: boolean;
	canSendToModeling?: boolean;
	canUndoCase: boolean;
	canWebExport: boolean;
	caseAssessmentLink: string;
	caseState?: ICaseState;
	caseTypeDescription: string;
	caseTypeId: number;
	chartNumber: string;
	cloudStorageFileType: string;
	companyId: number;
	dateModified: string;
	designSharedMessageId: string;
	doctorId: number;
	doctorName: string;
	exportFileLink: string;
	groupedStatus: GroupedStatus;
	guid: string;
	iGoCaseAssessmentEnabled: boolean;
	iGoCaseAssessmentLink: string;
	iGoOutcomeSimulatorEnabled: boolean;
	iGoOutcomeSimulatorLink: string;
	iGoProgressAssessmentEnabled: boolean;
	id: number;
	ioSimPlusEnabled: boolean;
	isCaptureFileExist: boolean;
	isChecked?: boolean;
	isDesignShared: boolean;
	isEvxEnabled: boolean;
	isFailedToSend: boolean;
	isItero360Enabled: boolean;
	isMyDesignAppEnabled: boolean
	isNiriOrder: boolean;
	isOrder: boolean;
	isOrderDownloaded: boolean;
	isOrderPrinted: boolean;
	isOrthoOrder: boolean;
	isQualifiedForIoSimPlus: boolean = true;
	isScanImagesZipFileExist: boolean;
	isTakenByScanner: boolean;
	lastBowId: number;
	lastDetailsId: number;
	lastWorkOrderDateCreated: string;
	openFileLink: string;
	orderCode: string;
	outcomeSimulatorEnabled: boolean;
	outcomeSimulatorLink: string;
	patientName: string;
	procedureDescription?: string;
	procedureId?: number;
	procedureMapId?: number;
	procedureTypeId?: number;
	progressAssessmentEnabled: boolean;
	progressAssessmentLink: string;
	progressTrackingLink: string;
	referral: ReferralResponse;
	rxVersion: number;
	scanDate: string;
	scanImagesHasOnlyColoredImages: boolean;
	serverEndpoint: string;
	shipToPartnerId: number;
	shipToPartnerName: string;
	simulationInfo: SimulationInfo;
	status: string;
	statusAndState: string;
	toothNumberingSystem: ToothNumberingSystem;
	statusId: number;
	originalOrderId?: number;
	originalInitiatorCompanyName?: string;
	originalInitiatorDoctorName?: string;
}
